import React from "react";
import { Alert, Divider, Table, Tag } from "antd";
import * as XLSXStyle from "xlsx-js-style";
import {
  CheckCircleOutlined,
  SwapOutlined,
  ScissorOutlined,
  PauseCircleOutlined,
  MailOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { ModalItem, ButtonItem } from "../../../../../Components/Items";
import {
  celdaFecha,
  celdaTexto,
  headerStyle,
} from "../../../../../Components/ExcelEstilos";
import { ESTADOS, colorEstado, etiquetaEstado } from "./analizarLibrosDigitales";

//Los desenlaces en los que NO se tocó nada. Son los que el usuario tiene que trabajar a
//mano, así que además de venir en la hoja general van en una hoja aparte.
const ESTADOS_SIN_TOCAR = ["otroCliente", "timbrada", "enGlobal", "error", "detenida"];

const COLUMNAS = [
  { titulo: "Fila", ancho: 8, valor: (r) => r.numeroFila },
  { titulo: "Matricula", ancho: 16, valor: (r) => r.matricula },
  { titulo: "Alumno", ancho: 30, valor: (r) => r.alumno },
  { titulo: "Codigo del libro", ancho: 20, valor: (r) => r.numeroLote },
  { titulo: "Libro", ancho: 30, valor: (r) => r.insumo },
  { titulo: "Resultado", ancho: 24, valor: (r) => etiquetaEstado(r.estado) },
  { titulo: "Folio de la venta", ancho: 18, valor: (r) => r.folio },
  { titulo: "Cliente de la venta", ancho: 30, valor: (r) => r.clienteVenta },
  { titulo: "RFC", ancho: 16, valor: (r) => r.rfcVenta },
  { titulo: "UUID", ancho: 38, valor: (r) => r.uuid },
  { titulo: "Fecha de la venta", ancho: 16, valor: (r) => r.fechaVenta, fecha: true },
  { titulo: "Codigos entregados", ancho: 24, valor: (r) => r.codigo },
  { titulo: "Se envio a", ancho: 32, valor: (r) => r.destinatarios || r.correo },
  { titulo: "Correo enviado", ancho: 14, valor: (r) => (r.correoEnviado ? "Si" : "No") },
  { titulo: "Motivo", ancho: 60, valor: (r) => r.motivo },
  // Cuánto tardó el renglón y en qué paso: si la importación se vuelve lenta, aquí se ve dónde.
  { titulo: "Segundos", ancho: 10, valor: (r) => (typeof r.segundos === "number" ? String(r.segundos) : "") },
  { titulo: "Tiempos por paso (s)", ancho: 60, valor: (r) => r.tiempos || "" },
];

const minutosYSegundos = (segundos) => {
  const total = Math.round(Number(segundos) || 0);
  const minutos = Math.floor(total / 60);
  return minutos > 0 ? `${minutos} min ${total % 60} s` : `${total} s`;
};

const textoDuracion = (duracion) => {
  if (!duracion || !duracion.segundosTotal) return "";
  const lentas = (duracion.etapasMasLentas || [])
    .map((valueEtapa) => `${valueEtapa.etapa} ${valueEtapa.segundos} s`)
    .join(", ");
  return `Tardó ${minutosYSegundos(duracion.segundosTotal)} · ${duracion.segundosPorRenglon} s por renglón en promedio${lentas ? ` · lo más lento: ${lentas}` : ""}`;
};

const hojaDeResultados = (filas) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([
    COLUMNAS.map((columna) => ({ v: columna.titulo, t: "s", s: headerStyle })),
    ...filas.map((fila) =>
      COLUMNAS.map((columna) => {
        const valor = columna.valor(fila);
        return columna.fecha ? celdaFecha(valor) : celdaTexto(valor ?? "");
      }),
    ),
  ]);
  hoja["!cols"] = COLUMNAS.map((columna) => ({ wch: columna.ancho }));
  hoja["!rows"] = [{ hpt: 26 }];
  return hoja;
};

const descargarExcel = (resultados) => {
  const libro = XLSXStyle.utils.book_new();

  XLSXStyle.utils.book_append_sheet(libro, hojaDeResultados(resultados), "Resultados");

  const sinTocar = resultados.filter((valueRes) =>
    ESTADOS_SIN_TOCAR.includes(valueRes.estado),
  );

  //Siempre se agrega la hoja, aunque venga vacía: que exista y esté en blanco dice "no
  //quedó nada pendiente", que es información. Una hoja ausente parece un error del reporte.
  XLSXStyle.utils.book_append_sheet(
    libro,
    hojaDeResultados(sinTocar),
    "Sin reasignar",
  );

  XLSXStyle.writeFile(libro, "resultadosVentasLibrosDigitales.xlsx");
};

const RESUMEN = [
  {
    key: "creada",
    label: "Ventas creadas",
    icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
  },
  {
    key: "reasignada",
    label: "Ventas reasignadas al alumno",
    icon: <SwapOutlined style={{ color: "#1890ff" }} />,
  },
  {
    key: "separada",
    label: "Separadas de una venta compartida en una venta propia del alumno",
    icon: <ScissorOutlined style={{ color: "#2f54eb" }} />,
  },
  {
    key: "yaAsignada",
    label: "Ya estaban asignadas (sólo se reenvió el correo)",
    icon: <MailOutlined style={{ color: "#13c2c2" }} />,
  },
  {
    key: "agrupada",
    label: "Códigos agregados a la venta de otra fila",
    icon: <CheckCircleOutlined style={{ color: "#722ed1" }} />,
  },
  {
    key: "otroCliente",
    label: "De otro cliente (no se tocaron)",
    icon: <WarningOutlined style={{ color: "#fa8c16" }} />,
  },
  {
    key: "timbrada",
    label: "Timbradas (no se tocaron)",
    icon: <WarningOutlined style={{ color: "#d4380d" }} />,
  },
  {
    key: "enGlobal",
    label: "Dentro de una Factura Global (no se tocaron)",
    icon: <WarningOutlined style={{ color: "#d4380d" }} />,
  },
  {
    key: "error",
    label: "Con error",
    icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
  },
  {
    key: "detenida",
    label: "No se procesaron (se detuvo la importación)",
    icon: <PauseCircleOutlined style={{ color: "#8c8c8c" }} />,
  },
];

const ResultadosLibrosDigitales = ({ visible, resultado, cerrarModal }) => {
  const resultados = resultado?.resultados || [];

  const cuentaPor = (estado) => resultados.filter((r) => r.estado === estado).length;

  const sinTocar = resultados.filter(
    (r) => ESTADOS_SIN_TOCAR.includes(r.estado) && r.estado !== "error" && r.estado !== "detenida",
  );
  const conCodigo = resultados.filter((r) => ESTADOS[r.estado]?.escribe || r.estado === "yaAsignada");
  const sinCorreo = conCodigo.filter((r) => !r.correoEnviado);

  const columnas = [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula", width: 120 },
    { title: "Alumno", dataIndex: "alumno", key: "alumno", width: 190 },
    { title: "Código", dataIndex: "numeroLote", key: "numeroLote", width: 150 },
    {
      title: "Resultado",
      key: "estado",
      width: 180,
      render: (_, record) => (
        <Tag color={colorEstado(record.estado)}>{etiquetaEstado(record.estado)}</Tag>
      ),
    },
    { title: "Folio", dataIndex: "folio", key: "folio", width: 130 },
    { title: "Cliente de la venta", dataIndex: "clienteVenta", key: "clienteVenta", width: 190 },
    {
      title: "Correo",
      key: "correoEnviado",
      width: 100,
      align: "center",
      render: (_, record) =>
        ESTADOS[record.estado]?.escribe || record.estado === "yaAsignada" ? (
          <Tag color={record.correoEnviado ? "green" : "orange"}>
            {record.correoEnviado ? "Enviado" : "No"}
          </Tag>
        ) : null,
    },
    { title: "Motivo", dataIndex: "motivo", key: "motivo", width: 340 },
    { title: "Seg.", dataIndex: "segundos", key: "segundos", width: 70, align: "right" },
  ];

  return (
    <ModalItem
      title="Resultado de la importación"
      visible={visible}
      spinning={false}
      onCancel={cerrarModal}
      width={1200}
    >
      {resultado && (
        <>
          {resultado.detenida && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Se detuvo la importación"
              description="Los renglones que no alcanzó a procesar salen como «No se procesó (detenida)». Vuelve a subir el mismo archivo y sólo se procesa lo que faltó."
            />
          )}

          {(resultado.interrumpida || resultado.terminado === false) && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="La importación se interrumpió antes de terminar"
              description={`${resultado.error ? `${resultado.error}. ` : ""}Abajo está lo que sí se procesó (${resultados.length} de ${resultado.total || "?"} renglones). Vuelve a subir el mismo archivo: lo que ya quedó hecho no se repite.`}
            />
          )}

          {textoDuracion(resultado.duracion) && (
            <div style={{ marginBottom: 8, color: "#595959" }}>
              {textoDuracion(resultado.duracion)}
            </div>
          )}

          <div>
            {RESUMEN.filter((fila) => cuentaPor(fila.key) > 0).map((fila) => (
              <div
                key={fila.key}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}
              >
                {fila.icon}
                <span style={{ flex: 1, color: "#595959" }}>{fila.label}</span>
                <span style={{ fontWeight: 500 }}>{cuentaPor(fila.key)}</span>
              </div>
            ))}
          </div>

          {sinTocar.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`${sinTocar.length} ventas quedaron sin reasignar`}
              description='No se les cambió el cliente porque son de otro cliente, ya están timbradas o ya entraron en una Factura Global. Descarga los resultados: vienen en la hoja "Sin reasignar" con su folio, su cliente y su UUID para trabajarlas a mano.'
            />
          )}

          {sinCorreo.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`${sinCorreo.length} alumnos no recibieron el correo`}
              description="La venta y el código ya quedaron bien; sólo falló el envío. Revisa el motivo de cada uno en la tabla; volver a subir el mismo archivo reenvía el correo sin duplicar nada."
            />
          )}

          <Divider style={{ margin: "12px 0" }} />

          <Table
            size="small"
            rowKey={(record, index) => index}
            dataSource={resultados}
            columns={columnas}
            scroll={{ x: "max-content", y: 360 }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
          />

          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}
          >
            <ButtonItem
              onClick={() => descargarExcel(resultados)}
              disabled={resultados.length === 0}
            >
              Descargar Resultados
            </ButtonItem>
            <ButtonItem type="primary" onClick={cerrarModal}>
              Aceptar
            </ButtonItem>
          </div>
        </>
      )}
    </ModalItem>
  );
};

export default ResultadosLibrosDigitales;
